import { useEffect } from "react";

export default function Cookies(){
  useEffect(()=>{ document.title = "Cookie Policy | MikDok"; },[]);

  const cookies = [
    ["__session / _sessionid","MikDok","Maintains login session & security","Essential","Session / up to 30 days"],
    ["mikdok_pref_lang","MikDok","Language/UI preferences","Functional","6–12 months"],
    ["_ga / _ga_*","Google","Anonymous analytics (GA4)","Analytics","up to 24 months"],
    ["_gid","Google","Session analytics","Analytics","24 hours"],
    ["_gcl_au / IDE / ANID","Google","Ads/conversion (only if enabled)","Marketing","up to 24 months"],
    ["mikdok_consent","MikDok","Minimal flag that consent exists","Essential","12 months"]
  ];

  return (
    <main className="legal" style={page}>
      {/* Inline CSS with media queries for mobile */}
      <style>{css}</style>

      <h1 className="legal__h1">Cookie Policy</h1>
      <p className="legal__muted"><strong>Effective date:</strong> 13 November 2025</p>

      <p>
        This Cookie Policy explains how MikDok (“we”, “us”, “our”) uses cookies and similar technologies on
        <a className="legal__link" href="https://mikdok.com" target="_blank" rel="noreferrer"> mikdok.com</a>.
        Read this together with our <a className="legal__link" href="/privacy">Privacy Policy</a>.
      </p>

      <h2 className="legal__h2">1) What are cookies?</h2>
      <p>
        Cookies are small text files stored on your device. We may also use localStorage and SDK signals to keep you signed in,
        secure the platform, remember preferences, and—only with your consent—measure usage or ads.
      </p>

      <h2 className="legal__h2">2) Who sets cookies?</h2>
      <ul className="legal__ul">
        <li><strong>First-party</strong> (mikdok.com) — auth, security, preferences.</li>
        <li><strong>Third-party</strong> (e.g., Google/Firebase, maps, analytics) — only if you consent where required.</li>
      </ul>

      <h2 className="legal__h2">3) Categories</h2>
      <ul className="legal__ul">
        <li><strong>Essential</strong> — strictly necessary (authentication, security, load balancing). Always on.</li>
        <li><strong>Functional</strong> — remember language/UI preferences. Optional.</li>
        <li><strong>Analytics</strong> — aggregated usage insights (GA4/Firebase). Optional.</li>
        <li><strong>Marketing</strong> — ads/conversion measurement. Optional (off by default).</li>
      </ul>

      <h2 className="legal__h2">4) Legal basis</h2>
      <p>Essential cookies: legitimate interests. Others: your consent (GDPR Art. 6(1)(a)). You can change choices any time via “Cookie settings”.</p>

      <h2 className="legal__h2">5) Managing your choices</h2>
      <p>Open the banner via the footer link <em>Cookie settings</em> or use your browser settings. Blocking Essential cookies may break the site.</p>

      <h2 className="legal__h2">6) Typical cookies we use</h2>
      <div className="legal__callout">
        Names and expiries may vary by environment. We update this list periodically.
      </div>

      {/* Desktop/tablet: table */}
      <div className="cookies-table__wrapper">
        <table className="cookies-table" aria-label="Cookies table">
          <thead>
            <tr>
              <th style={th}>Name</th>
              <th style={th}>Provider</th>
              <th style={th}>Purpose</th>
              <th style={th}>Category</th>
              <th style={th}>Expiry</th>
            </tr>
          </thead>
          <tbody>
            {cookies.map((r,i)=>(
              <tr key={i}>
                {r.map((c,j)=>(
                  <td key={j} style={td}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="cookies-cards" aria-label="Cookies list (mobile)">
        {cookies.map((r,i)=>(
          <section className="cookie-card" key={`m-${i}`}>
            <div className="cookie-card__row">
              <span className="cookie-card__label">Name</span>
              <span className="cookie-card__value">{r[0]}</span>
            </div>
            <div className="cookie-card__row">
              <span className="cookie-card__label">Provider</span>
              <span className="cookie-card__value">{r[1]}</span>
            </div>
            <div className="cookie-card__row">
              <span className="cookie-card__label">Purpose</span>
              <span className="cookie-card__value">{r[2]}</span>
            </div>
            <div className="cookie-card__row">
              <span className="cookie-card__label">Category</span>
              <span className="cookie-card__badge">{r[3]}</span>
            </div>
            <div className="cookie-card__row">
              <span className="cookie-card__label">Expiry</span>
              <span className="cookie-card__value">{r[4]}</span>
            </div>
          </section>
        ))}
      </div>

      <h2 className="legal__h2">7) Third-party details</h2>
      <ul className="legal__ul">
        <li><strong>Firebase (Google Cloud, EU region):</strong> auth, Firestore, storage, security. Analytics only if enabled.</li>
        <li><strong>Google Analytics / Consent Mode v2:</strong> respects your consent for ad_storage, analytics_storage, etc.</li>
      </ul>

      <h2 className="legal__h2">8) Changes</h2>
      <p>We may update this Cookie Policy and will post a new effective date above.</p>

      <h2 className="legal__h2">9) Contact</h2>
      <p>Email: <a className="legal__link" href="mailto:contact@mikdok.al">contact@mikdok.al</a></p>

      <p><a className="legal__link" href="/privacy">Privacy Policy</a> · <a className="legal__link" href="/terms">Terms &amp; Conditions</a></p>
    </main>
  );
}

/* ====== Styles ====== */
const page = {
  maxWidth: 900,
  margin: "48px auto",
  padding: "0 22px",
  lineHeight: 1.7,
  color: "#0f172a",
  fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif"
};

const th = {border:"1px solid #e5e7eb",padding:"12px 10px",background:"#f8fafc",textAlign:"left",verticalAlign:"top",fontWeight:700};
const td = {border:"1px solid #e5e7eb",padding:"10px",verticalAlign:"top"};

const css = `
  .legal__h1{font-size:2rem;margin:0 0 8px;font-weight:800;letter-spacing:.2px}
  .legal__h2{font-size:1.2rem;margin:26px 0 8px;font-weight:700;letter-spacing:.2px}
  .legal__muted{color:#64748b}
  .legal__ul{padding-left:20px;margin:10px 0}
  .legal__link{color:#2563eb;text-decoration:none;padding:2px 2px;border-radius:6px}
  .legal__link:focus{outline:none;box-shadow:0 0 0 3px rgba(37,99,235,.25)}
  .legal__link:hover{text-decoration:underline}
  .legal__callout{background:#f1f5f9;border-left:4px solid #3b82f6;padding:12px;border-radius:10px;margin-bottom:12px}

  /* Desktop/tablet table visible ≥640px */
  .cookies-table__wrapper{display:block}
  .cookies-table{width:100%;border-collapse:collapse;margin:12px 0;font-size:0.975rem}

  /* Mobile cards hidden by default on wide screens */
  .cookies-cards{display:none}

  /* --- Mobile first tweaks --- */
  @media (max-width: 639px){
    .legal{padding:0 16px}
    .legal__h1{font-size:1.6rem;margin-top:6px}
    .legal__h2{font-size:1.05rem}
    .cookies-table__wrapper{display:none}        /* hide table on small screens */
    .cookies-cards{display:grid;gap:12px;margin:12px 0}
    .cookie-card{
      background:#ffffff;
      border:1px solid #e5e7eb;
      border-radius:12px;
      padding:12px;
      box-shadow:0 6px 20px rgba(0,0,0,.06);
    }
    .cookie-card__row{
      display:flex;
      justify-content:space-between;
      align-items:flex-start;
      gap:12px;
      padding:6px 0;
      border-bottom:1px dashed #eef2f7;
    }
    .cookie-card__row:last-child{border-bottom:0}
    .cookie-card__label{
      min-width:92px;
      color:#64748b;
      font-size:.92rem;
      line-height:1.35;
    }
    .cookie-card__value{
      flex:1;
      font-size:.98rem;
      line-height:1.45;
      word-break:break-word;
    }
    .cookie-card__badge{
      flex:0 0 auto;
      font-size:.85rem;
      background:#eef2ff;
      color:#3730a3;
      padding:2px 8px;
      border-radius:999px;
      line-height:1.6;
    }
  }

  /* Respect dark mode */
  @media (prefers-color-scheme: dark){
    .legal{color:#e5e7eb}
    .legal__muted{color:#94a3b8}
    .legal__link{color:#60a5fa}
    .legal__callout{background:rgba(148,163,184,.15);border-left-color:#60a5fa}
    .cookies-table th{background:rgba(255,255,255,.06);color:#e5e7eb}
    .cookies-table td,.cookies-table th{border-color:rgba(255,255,255,.12)}
    .cookie-card{background:rgba(31,41,55,.78);border-color:rgba(255,255,255,.12)}
    .cookie-card__row{border-bottom-color:rgba(255,255,255,.08)}
    .cookie-card__badge{background:rgba(99,102,241,.18);color:#c7d2fe}
  }
`;
